/**
 * Sesion y politica de operacion offline.
 *
 * Reglas implementadas:
 *  - La sesion offline SOLO existe a partir de una sesion previamente validada en linea.
 *  - Vence: pasado `offlineValidUntil` la aplicacion deja de permitir operar sin conexion.
 *  - Esta ligada a la instalacion autorizada; si el servidor la revoca, se corta.
 *  - Las acciones offline conservan la identidad del usuario que las realizo.
 *  - Al recuperar conexion, el servidor REVALIDA antes de aceptar la sincronizacion.
 *
 * Limite honesto de seguridad: en un navegador no existe almacenamiento inviolable.
 * Quien controle el dispositivo desbloqueado puede leer IndexedDB. Por eso se
 * minimiza lo que se guarda, no se guardan datos financieros sensibles, la sesion
 * offline caduca y el dispositivo es revocable desde administracion.
 * Ver `docs/SEGURIDAD.md`.
 */

import type { SessionUser } from '@senm/contracts';
import { createIdGenerator, isExpired } from '@senm/domain';
import { getMeta, setMeta } from './db.js';

const newId = createIdGenerator();

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
  offlineValidUntil: string;
  user: SessionUser;
  /** Ultima vez que el servidor confirmo que la sesion sigue autorizada. */
  lastRevalidatedAt: string;
}

const SESSION_KEY = 'sesion';
const INSTALLATION_KEY = 'instalacion';

/**
 * Identificador logico de la instalacion. Se genera una vez y se guarda localmente.
 * NO es una huella del dispositivo ni un identificador persistente del hardware:
 * borrar los datos del sitio genera uno nuevo, y administracion puede revocarlo.
 */
export async function getInstallationId(): Promise<string> {
  const existing = await getMeta<string | null>(INSTALLATION_KEY, null);
  if (existing !== null) return existing;
  const created = newId();
  await setMeta(INSTALLATION_KEY, created);
  return created;
}

export async function getSession(): Promise<StoredSession | null> {
  return getMeta<StoredSession | null>(SESSION_KEY, null);
}

export async function saveSession(session: StoredSession): Promise<void> {
  await setMeta(SESSION_KEY, session);
}

export async function clearSession(): Promise<void> {
  await setMeta(SESSION_KEY, null);
}

export type OfflineAccess =
  | { allowed: true; user: SessionUser }
  | { allowed: false; reason: 'SIN_SESION' | 'VENCIDA' };

/**
 * Decide si se puede seguir operando sin conexion. Es la unica puerta: ninguna
 * pantalla decide esto por su cuenta.
 */
export function evaluateOfflineAccess(session: StoredSession | null, now: string): OfflineAccess {
  if (session === null) return { allowed: false, reason: 'SIN_SESION' };
  if (isExpired(session.offlineValidUntil, now)) return { allowed: false, reason: 'VENCIDA' };
  return { allowed: true, user: session.user };
}

export function offlineRemainingHours(session: StoredSession, now: string): number {
  const ms = new Date(session.offlineValidUntil).getTime() - new Date(now).getTime();
  return Math.max(0, Math.floor(ms / 3_600_000));
}

/** Permisos efectivos de la sesion, para mostrar u ocultar interfaz. */
export function sessionCan(session: StoredSession | null, permission: string): boolean {
  return session?.user.permissions.includes(permission) ?? false;
}
